/**
 * Field Editor Components
 *
 * Specialized editors for different IDF field types.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import type { IDDField } from '../idd/types';
import type { FieldEditorProps, FieldValidation } from './types';
import { getFieldEditorType } from './types';
import {
  useOutputLookupStore,
  selectHasSimulationData,
  selectVariableCount,
  selectMeterCount,
  type VariableSuggestion,
} from '../stores/outputLookupStore';
import { DictionaryGeneratorButton } from './DictionaryGeneratorButton';
import { useDocumentation } from '@/docs';

// Icons
function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11 7h2v2h-2V7zm0 4h2v6h-2v-6zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
    </svg>
  );
}

function BookOpenIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

/**
 * Field wrapper with label, validation display, and documentation integration
 */
function FieldWrapper({
  fieldInfo,
  validation,
  objectType,
  children,
}: {
  fieldInfo: IDDField;
  validation: FieldValidation;
  objectType?: string;
  children: React.ReactNode;
}) {
  const [showMemo, setShowMemo] = useState(false);
  const { getFieldDocumentation, openDocsPanel, hasEngineeringRef } = useDocumentation();

  // Get enhanced documentation for this field
  const enhancedDocs = useMemo(() => {
    if (!objectType) return null;
    return getFieldDocumentation(objectType, fieldInfo.name || fieldInfo.id);
  }, [objectType, fieldInfo.name, fieldInfo.id, getFieldDocumentation]);

  // Check if this object has engineering reference
  const hasEngRef = useMemo(() => {
    if (!objectType) return false;
    return hasEngineeringRef(objectType);
  }, [objectType, hasEngineeringRef]);

  const handleOpenDocs = useCallback(() => {
    openDocsPanel(objectType, fieldInfo.name || fieldInfo.id);
  }, [openDocsPanel, objectType, fieldInfo.name, fieldInfo.id]);

  return (
    <div
      className={`group relative rounded-md border p-3 transition-colors ${
        validation.status === 'error'
          ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
          : validation.status === 'warning'
            ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20'
            : 'border-border bg-background hover:border-muted-foreground/30'
      }`}
    >
      {/* Label row */}
      <div className="mb-1.5 flex items-center justify-between">
        <label className="flex items-center gap-1 text-sm font-medium text-foreground">
          {fieldInfo.name || fieldInfo.id}
          {fieldInfo.required && <span className="text-red-500 dark:text-red-400">*</span>}
        </label>
        <div className="flex items-center gap-1">
          {/* Engineering reference indicator */}
          {hasEngRef && (
            <span
              className="text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-900/50"
              onClick={handleOpenDocs}
              title="Engineering reference available"
            >
              Eng
            </span>
          )}
          {/* Docs button - always show if there's documentation */}
          {(fieldInfo.memo || enhancedDocs?.engineeringDocument) && (
            <button
              onClick={() => setShowMemo(!showMemo)}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Show field documentation"
            >
              <InfoIcon className="h-4 w-4" />
            </button>
          )}
          {/* Open full docs panel */}
          {objectType && (
            <button
              onClick={handleOpenDocs}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-primary"
              title="Open documentation panel"
            >
              <BookOpenIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Field input */}
      {children}

      {/* Field metadata */}
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {fieldInfo.default !== undefined && (
          <span>Default: {fieldInfo.default}</span>
        )}
        {fieldInfo.units && <span className="font-medium">{fieldInfo.units}</span>}
        {(fieldInfo.minimum !== undefined || fieldInfo.maximum !== undefined) && (
          <span>
            Range: {fieldInfo.minimum ?? '-∞'} to {fieldInfo.maximum ?? '∞'}
          </span>
        )}
        {(fieldInfo.autosizable || fieldInfo.autocalculatable) && (
          <span className="rounded bg-blue-100 dark:bg-blue-900/30 px-1 text-blue-700 dark:text-blue-400">
            {fieldInfo.autosizable ? 'autosize' : 'autocalculate'}
          </span>
        )}
      </div>

      {/* Documentation popup */}
      {showMemo && (fieldInfo.memo || enhancedDocs) && (
        <div className="mt-2 rounded-md bg-blue-50 dark:bg-blue-900/30 p-2 text-xs text-blue-800 dark:text-blue-300 space-y-2">
          {fieldInfo.memo && <p>{fieldInfo.memo}</p>}
          {enhancedDocs?.relatedObjectsList && enhancedDocs.relatedObjectsList.length > 0 && (
            <div className="pt-1 border-t border-blue-200 dark:border-blue-800">
              <span className="font-medium">Related objects: </span>
              {enhancedDocs.relatedObjectsList.join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Validation messages */}
      {validation.errors.length > 0 && (
        <div className="mt-2 space-y-1">
          {validation.errors.map((err, i) => (
            <div key={i} className="flex items-start gap-1 text-xs text-red-600 dark:text-red-400">
              <svg className="mt-0.5 h-3 w-3 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
              <span>{err.message}</span>
            </div>
          ))}
        </div>
      )}

      {validation.warnings.length > 0 && (
        <div className="mt-2 space-y-1">
          {validation.warnings.map((warn, i) => (
            <div key={i} className="flex items-start gap-1 text-xs text-amber-600 dark:text-amber-400">
              <svg className="mt-0.5 h-3 w-3 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
              </svg>
              <span>{warn.message}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Text field editor for alpha-type fields
 */
export function TextFieldEditor({
  object,
  field,
  fieldInfo,
  validation,
  onChange,
  disabled,
}: FieldEditorProps) {
  return (
    <FieldWrapper fieldInfo={fieldInfo} validation={validation} objectType={object.className}>
      <input
        type="text"
        value={field?.value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={fieldInfo.default || 'Enter value...'}
        className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring disabled:bg-muted disabled:text-muted-foreground"
      />
    </FieldWrapper>
  );
}

/**
 * Number field editor for real/integer-type fields
 */
export function NumberFieldEditor({
  object,
  field,
  fieldInfo,
  validation,
  onChange,
  disabled,
}: FieldEditorProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  const step = fieldInfo.type === 'integer' ? 1 : 0.01;

  return (
    <FieldWrapper fieldInfo={fieldInfo} validation={validation} objectType={object.className}>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={field?.value ?? ''}
          onChange={handleChange}
          disabled={disabled}
          step={step}
          min={fieldInfo.minimum}
          max={fieldInfo.maximum}
          placeholder={fieldInfo.default?.toString() || '0'}
          className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring disabled:bg-muted disabled:text-muted-foreground"
        />
        {fieldInfo.units && (
          <span className="rounded bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
            {fieldInfo.units}
          </span>
        )}
      </div>
    </FieldWrapper>
  );
}

/**
 * Choice field editor for fields with predefined choices
 */
export function ChoiceFieldEditor({
  object,
  field,
  fieldInfo,
  validation,
  onChange,
  disabled,
}: FieldEditorProps) {
  const choices = fieldInfo.choices || [];

  return (
    <FieldWrapper fieldInfo={fieldInfo} validation={validation} objectType={object.className}>
      <select
        value={field?.value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring disabled:bg-muted disabled:text-muted-foreground"
      >
        {!fieldInfo.required && <option value="">(not specified)</option>}
        {choices.map((choice) => (
          <option key={choice} value={choice}>
            {choice}
          </option>
        ))}
      </select>
    </FieldWrapper>
  );
}

/**
 * Reference field editor for fields that reference other objects
 */
export function ReferenceFieldEditor({
  object,
  field,
  fieldInfo,
  validation,
  referencedObjects,
  onChange,
  disabled,
}: FieldEditorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredObjects = useMemo(() => {
    if (!search.trim()) return referencedObjects;
    const query = search.toLowerCase();
    return referencedObjects.filter(
      (obj) =>
        obj.fields[0]?.value?.toLowerCase().includes(query) ||
        obj.className.toLowerCase().includes(query)
    );
  }, [referencedObjects, search]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (objectName: string) => {
      onChange(objectName);
      setSearch('');
      setIsOpen(false);
    },
    [onChange]
  );

  return (
    <FieldWrapper fieldInfo={fieldInfo} validation={validation} objectType={object.className}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? search : field?.value ?? ''}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          disabled={disabled}
          placeholder={`Select ${fieldInfo.objectList || 'object'}...`}
          className="w-full rounded-md border border-input bg-background px-3 py-1.5 pr-8 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring disabled:bg-muted disabled:text-muted-foreground"
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
          </svg>
        </button>

        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-md border border-border bg-popover shadow-lg"
          >
            {!fieldInfo.required && (
              <button
                onClick={() => handleSelect('')}
                className="w-full px-3 py-2 text-left text-sm text-muted-foreground hover:bg-muted"
              >
                (none)
              </button>
            )}
            {filteredObjects.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                No matching objects found
              </div>
            ) : (
              filteredObjects.slice(0, 50).map((obj) => {
                const objName = obj.fields[0]?.value || obj.className;
                return (
                  <button
                    key={obj.id}
                    onClick={() => handleSelect(objName)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-primary/10"
                  >
                    <span className="flex-1 truncate text-sm text-foreground">
                      {objName}
                    </span>
                    <span className="text-xs text-muted-foreground">{obj.className}</span>
                  </button>
                );
              })
            )}
            {filteredObjects.length > 50 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">
                + {filteredObjects.length - 50} more...
              </div>
            )}
          </div>
        )}
      </div>
    </FieldWrapper>
  );
}

/**
 * Node field editor for HVAC node connections
 */
export function NodeFieldEditor({
  object,
  field,
  fieldInfo,
  validation,
  onChange,
  disabled,
}: FieldEditorProps) {
  // Determine node type from field name
  const fieldNameLower = (fieldInfo.name || '').toLowerCase();
  const isInlet = fieldNameLower.includes('inlet');
  const isOutlet = fieldNameLower.includes('outlet');

  return (
    <FieldWrapper fieldInfo={fieldInfo} validation={validation} objectType={object.className}>
      <div className="flex items-center gap-2">
        {isInlet && (
          <span className="rounded bg-blue-100 dark:bg-blue-900/30 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-400">
            IN
          </span>
        )}
        {isOutlet && (
          <span className="rounded bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 text-xs font-medium text-green-700 dark:text-green-400">
            OUT
          </span>
        )}
        <input
          type="text"
          value={field?.value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="Node name..."
          className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring disabled:bg-muted disabled:text-muted-foreground"
        />
      </div>
    </FieldWrapper>
  );
}

/**
 * Autocalculatable field editor with toggle
 */
export function AutocalculatableFieldEditor({
  object,
  field,
  fieldInfo,
  validation,
  onChange,
  disabled,
}: FieldEditorProps) {
  const value = field?.value ?? '';
  const isAuto = value.toLowerCase() === 'autocalculate' || (!field && fieldInfo.default === 'autocalculate');

  const handleToggle = useCallback(() => {
    if (isAuto) {
      onChange('');
    } else {
      onChange('autocalculate');
    }
  }, [isAuto, onChange]);

  return (
    <FieldWrapper fieldInfo={fieldInfo} validation={validation} objectType={object.className}>
      <div className="space-y-2">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isAuto}
            onChange={handleToggle}
            disabled={disabled}
            className="rounded border-input text-primary focus:ring-ring"
          />
          <span className="text-sm text-muted-foreground">Autocalculate</span>
        </label>

        {!isAuto && (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              step={fieldInfo.type === 'integer' ? 1 : 0.01}
              min={fieldInfo.minimum}
              max={fieldInfo.maximum}
              placeholder="Enter value..."
              className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring disabled:bg-muted disabled:text-muted-foreground"
            />
            {fieldInfo.units && (
              <span className="rounded bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
                {fieldInfo.units}
              </span>
            )}
          </div>
        )}
      </div>
    </FieldWrapper>
  );
}

/**
 * Autosizable field editor with toggle
 */
export function AutosizableFieldEditor({
  object,
  field,
  fieldInfo,
  validation,
  onChange,
  disabled,
}: FieldEditorProps) {
  const value = field?.value ?? '';
  const isAuto = value.toLowerCase() === 'autosize' || (!field && fieldInfo.default === 'autosize');

  const handleToggle = useCallback(() => {
    if (isAuto) {
      onChange('');
    } else {
      onChange('autosize');
    }
  }, [isAuto, onChange]);

  return (
    <FieldWrapper fieldInfo={fieldInfo} validation={validation} objectType={object.className}>
      <div className="space-y-2">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isAuto}
            onChange={handleToggle}
            disabled={disabled}
            className="rounded border-input text-primary focus:ring-ring"
          />
          <span className="text-sm text-muted-foreground">Autosize</span>
        </label>

        {!isAuto && (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              step={fieldInfo.type === 'integer' ? 1 : 0.01}
              min={fieldInfo.minimum}
              max={fieldInfo.maximum}
              placeholder="Enter value..."
              className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring disabled:bg-muted disabled:text-muted-foreground"
            />
            {fieldInfo.units && (
              <span className="rounded bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
                {fieldInfo.units}
              </span>
            )}
          </div>
        )}
      </div>
    </FieldWrapper>
  );
}

/**
 * External list field editor for Output:Variable and Output:Meter fields
 * Provides autocomplete with pre-built database + simulation-specific variables
 */
export function ExternalListFieldEditor({
  field,
  fieldInfo,
  validation,
  onChange,
  disabled,
}: FieldEditorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get the external list type to determine which suggestions to show
  const externalListType = fieldInfo.externalList?.toLowerCase() ?? '';
  // Check for variable vs meter - be specific to avoid "autorddmeter" matching both
  const isVariable = externalListType.includes('variable') || externalListType === 'autordd';
  const isMeter = externalListType.includes('meter') || externalListType === 'automdd';

  // Get suggestions from the store
  const getVariableSuggestions = useOutputLookupStore((state) => state.getVariableSuggestions);
  const getMeterSuggestions = useOutputLookupStore((state) => state.getMeterSuggestions);
  // Use selectors that compute from actual data to avoid stale persisted values
  const hasSimulationData = useOutputLookupStore(selectHasSimulationData);
  const variableCount = useOutputLookupStore(selectVariableCount);
  const meterCount = useOutputLookupStore(selectMeterCount);

  // Filter suggestions based on search
  const suggestions = useMemo(() => {
    const query = isOpen ? search : '';
    if (isVariable) {
      return getVariableSuggestions(query);
    } else if (isMeter) {
      return getMeterSuggestions(query);
    }
    return [];
  }, [isOpen, search, isVariable, isMeter, getVariableSuggestions, getMeterSuggestions]);

  // Group suggestions by category
  const groupedSuggestions = useMemo(() => {
    const groups = new Map<string, VariableSuggestion[]>();

    // Prioritize simulation results at the top
    const simulationResults: VariableSuggestion[] = [];
    const databaseResults: VariableSuggestion[] = [];

    for (const suggestion of suggestions) {
      if (suggestion.source === 'simulation') {
        simulationResults.push(suggestion);
      } else {
        databaseResults.push(suggestion);
      }
    }

    if (simulationResults.length > 0) {
      groups.set('From Your Model (Simulation Results)', simulationResults);
    }

    // Group database results by category
    for (const suggestion of databaseResults) {
      const existing = groups.get(suggestion.category) ?? [];
      existing.push(suggestion);
      groups.set(suggestion.category, existing);
    }

    return groups;
  }, [suggestions]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (name: string) => {
      onChange(name);
      setSearch('');
      setIsOpen(false);
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearch(value);
      onChange(value); // Also update the field value as user types
      if (!isOpen) setIsOpen(true);
    },
    [onChange, isOpen]
  );

  const totalSuggestions = suggestions.length;
  const displayLimit = 100;

  return (
    <FieldWrapper fieldInfo={fieldInfo} validation={validation}>
      <div className="relative">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={isOpen ? search : field?.value ?? ''}
            onChange={handleInputChange}
            onFocus={() => {
              setSearch(field?.value ?? '');
              setIsOpen(true);
            }}
            disabled={disabled}
            placeholder={isVariable ? 'Search output variables...' : isMeter ? 'Search meters...' : 'Enter value...'}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
          />
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
            </svg>
          </button>
        </div>

        {/* Data source indicator and dictionary generator */}
        {hasSimulationData ? (
          <div className="mt-1 flex items-center justify-between">
            <span className="inline-flex items-center gap-1 text-xs text-green-600">
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
              {isVariable
                ? `${variableCount} model-specific variables available`
                : `${meterCount} model-specific meters available`}
            </span>
            <DictionaryGeneratorButton compact onComplete={() => setIsOpen(false)} />
          </div>
        ) : (
          <div className="mt-2 flex items-center gap-2">
            <DictionaryGeneratorButton compact onComplete={() => setIsOpen(false)} />
            <span className="text-xs text-gray-500">
              to get accurate options for your model
            </span>
          </div>
        )}

        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute left-0 right-0 top-full z-10 mt-1 max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
          >
            {totalSuggestions === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                {search ? (
                  <>
                    No matching {isVariable ? 'variables' : 'meters'} found for "{search}"
                    <div className="mt-1 text-xs">
                      You can still enter a custom value
                    </div>
                  </>
                ) : (
                  <>
                    Start typing to search {isVariable ? 'output variables' : 'meters'}
                  </>
                )}
              </div>
            ) : (
              <>
                {Array.from(groupedSuggestions.entries())
                  .slice(0, displayLimit)
                  .map(([category, items]) => (
                    <div key={category}>
                      <div className="sticky top-0 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600 border-b border-gray-100">
                        {category}
                        {category === 'From Your Model (Simulation Results)' && (
                          <span className="ml-2 inline-flex items-center rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">
                            Model-specific
                          </span>
                        )}
                      </div>
                      {items.slice(0, 25).map((suggestion, idx) => (
                        <button
                          key={`${category}-${idx}`}
                          onClick={() => handleSelect(suggestion.name)}
                          className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-blue-50 border-b border-gray-50 last:border-b-0"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="truncate text-sm font-medium text-gray-900">
                              {suggestion.name}
                            </div>
                            <div className="truncate text-xs text-gray-500">
                              {suggestion.description}
                            </div>
                          </div>
                          {suggestion.units && (
                            <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                              {suggestion.units}
                            </span>
                          )}
                          {suggestion.isModelSpecific && (
                            <span className="flex-shrink-0 rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700">
                              ✓
                            </span>
                          )}
                        </button>
                      ))}
                      {items.length > 25 && (
                        <div className="px-3 py-1 text-xs text-gray-400 bg-gray-50">
                          + {items.length - 25} more in this category
                        </div>
                      )}
                    </div>
                  ))}
                {totalSuggestions > displayLimit && (
                  <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-t">
                    Showing {displayLimit} of {totalSuggestions} results. Type to narrow down.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </FieldWrapper>
  );
}

/**
 * Main field editor that selects appropriate editor based on field type
 */
export function FieldEditor(props: FieldEditorProps) {
  const editorType = getFieldEditorType(props.fieldInfo);

  // Check for external-list type (Output:Variable, Output:Meter)
  if (props.fieldInfo.type === 'external-list' && props.fieldInfo.externalList) {
    return <ExternalListFieldEditor {...props} />;
  }

  switch (editorType) {
    case 'number':
      return <NumberFieldEditor {...props} />;
    case 'choice':
      return <ChoiceFieldEditor {...props} />;
    case 'reference':
      return <ReferenceFieldEditor {...props} />;
    case 'node':
      return <NodeFieldEditor {...props} />;
    case 'autocalculatable':
      return <AutocalculatableFieldEditor {...props} />;
    case 'autosizable':
      return <AutosizableFieldEditor {...props} />;
    case 'text':
    default:
      return <TextFieldEditor {...props} />;
  }
}
